// Názvy dielcov s predvoľbou hrán — sekcia Nastavení (z NastaveniaPage, KOLO 170).
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateNazovDielca,
  useDeleteNazovDielca,
  useNazvyDielcov,
  useUpdateNazovDielca,
} from "@/api/hooks/useNazvyDielcov";
import type { NazovDielca } from "@/api/schemas";

/** Hrúbky hrán ponúkané v selectoch presetu (— = bez hrany). */
const PRESET_HRUBKY_HRAN = ["0.5", "1", "2"] as const;
const BEZ_HRANY = "__none__";

export function NazvyDielcovSection(): JSX.Element {
  const query = useNazvyDielcov();
  const nazvy = query.data?.data ?? [];
  const createMutation = useCreateNazovDielca();
  const updateMutation = useUpdateNazovDielca();
  const deleteMutation = useDeleteNazovDielca();
  const [novyNazov, setNovyNazov] = useState("");

  async function handleAdd(): Promise<void> {
    const nazov = novyNazov.trim();
    if (!nazov) return;
    try {
      await createMutation.mutateAsync({ nazov, poradie: nazvy.length });
      setNovyNazov("");
      toast({ title: "Názov pridaný", description: nazov });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(n: NazovDielca): Promise<void> {
    if (!window.confirm(`Zmazať názov "${n.nazov}"?`)) return;
    try {
      await deleteMutation.mutateAsync(n.id);
      toast({ title: "Názov zmazaný", description: n.nazov });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function setHrana(
    n: NazovDielca,
    field: "hrana_x1_mm" | "hrana_x2_mm" | "hrana_y1_mm" | "hrana_y2_mm",
    value: string,
  ): Promise<void> {
    try {
      await updateMutation.mutateAsync({
        id: n.id,
        data: { [field]: value === BEZ_HRANY ? null : value },
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  function hranaValue(v: string | null | undefined): string {
    if (v == null) return BEZ_HRANY;
    const num = parseFloat(String(v));
    return Number.isFinite(num) ? String(num) : BEZ_HRANY;
  }

  const SIDES = [
    { field: "hrana_x1_mm", label: "X1" },
    { field: "hrana_x2_mm", label: "X2" },
    { field: "hrana_y1_mm", label: "Y1" },
    { field: "hrana_y2_mm", label: "Y2" },
  ] as const;

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-xl">Názvy dielcov</h2>
        <p className="text-xs text-muted-foreground">
          Prednastavené názvy ponúkané pri zadávaní dielca. Pri výbere
          názvu sa dielcu automaticky priradia hrany podľa hrúbok nižšie
          (— = strana bez hrany). Šírka hrany sa odvodí z hrúbky dosky.
        </p>
      </div>

      <div className="flex items-end gap-2 max-w-md">
        <div className="flex-1 space-y-1">
          <Label htmlFor="nd-novy">Nový názov</Label>
          <Input
            id="nd-novy"
            value={novyNazov}
            onChange={(e) => setNovyNazov(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleAdd();
              }
            }}
            placeholder="polica_sklo"
          />
        </div>
        <Button
          size="sm"
          onClick={() => void handleAdd()}
          disabled={createMutation.isPending || !novyNazov.trim()}
        >
          <Plus className="h-4 w-4" />
          Pridať
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Načítavam…</p>
      ) : nazvy.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Žiadne názvy — pridaj prvý vyššie.
        </p>
      ) : (
        <div className="max-h-96 overflow-y-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Názov</TableHead>
                {SIDES.map((s) => (
                  <TableHead key={s.field} className="w-[110px] text-center">
                    Hrana {s.label}
                  </TableHead>
                ))}
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {nazvy.map((n) => (
                <TableRow key={n.id}>
                  <TableCell className="font-mono text-sm">{n.nazov}</TableCell>
                  {SIDES.map((s) => (
                    <TableCell key={s.field} className="p-1 text-center">
                      <Select
                        value={hranaValue(n[s.field])}
                        onValueChange={(v) => void setHrana(n, s.field, v)}
                      >
                        <SelectTrigger
                          className="h-7 w-full text-xs font-mono"
                          aria-label={`${n.nazov} hrana ${s.label}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={BEZ_HRANY} className="text-xs">
                            —
                          </SelectItem>
                          {PRESET_HRUBKY_HRAN.map((h) => (
                            <SelectItem key={h} value={h} className="text-xs">
                              {h} mm
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  ))}
                  <TableCell className="p-1 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => void handleDelete(n)}
                      disabled={deleteMutation.isPending}
                      aria-label={`Zmazať názov ${n.nazov}`}
                      title="Zmazať názov dielca"
                      className="h-7 w-7 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
